import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Calendar from 'react-calendar';
import { format } from 'date-fns';
import { fetchTimerBreakdown } from '../../redux/Slices/timerSlice';
import { formatDurationMs } from '../../utils/formatDuration';
import Checkbox from '../Global/Checkbox';
import 'react-calendar/dist/Calendar.css';
import './scss/TimerBreakdownPopover.scss';

const fmtMs = (ms) => formatDurationMs(ms, { style: 'compact' });

const formatDateLabel = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (dateStr === today.toISOString().slice(0, 10)) return 'Today';
    if (dateStr === yesterday.toISOString().slice(0, 10)) return 'Yesterday';
    return format(date, date.getFullYear() !== today.getFullYear() ? 'MMM d, yyyy' : 'MMM d');
};

const TimerBreakdownPopover = ({ cardId }) => {
    const dispatch = useDispatch();
    const breakdown = useSelector((state) => state.timer.timerBreakdowns?.[cardId]);

    const [range, setRange] = useState(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedDays, setSelectedDays] = useState(new Set());
    const [expandedDays, setExpandedDays] = useState(new Set());

    const load = useCallback((r) => {
        const startDate = r?.[0] ? format(r[0], 'yyyy-MM-dd') : undefined;
        const endDate = r?.[1] ? format(r[1], 'yyyy-MM-dd') : undefined;
        dispatch(fetchTimerBreakdown({ cardId, startDate, endDate }));
    }, [dispatch, cardId]);

    useEffect(() => { load(null); }, []);

    const handleRangeChange = (value) => {
        setRange(value);
        setShowCalendar(false);
        setSelectedDays(new Set());
        setExpandedDays(new Set());
        load(value);
    };

    const clearFilter = () => {
        setRange(null);
        setShowCalendar(false);
        setSelectedDays(new Set());
        setExpandedDays(new Set());
        load(null);
    };

    const toggleDay = (date) => {
        setSelectedDays((prev) => {
            const next = new Set(prev);
            if (next.has(date)) next.delete(date);
            else next.add(date);
            return next;
        });
    };

    const toggleExpandedDay = (date) => {
        setExpandedDays((prev) => {
            const next = new Set(prev);
            if (next.has(date)) next.delete(date);
            else next.add(date);
            return next;
        });
    };

    const days = breakdown?.days || [];
    const isFiltered = range !== null;

    const rangeLabel = isFiltered
        ? `${format(range[0], 'MMM d')} – ${format(range[1], 'MMM d, yyyy')}`
        : 'All time';

    const selectionMs = days
        .filter((d) => selectedDays.has(d.date))
        .reduce((sum, d) => sum + d.totalDuration, 0);
    const selectionCount = selectedDays.size;
    const selectionAvg = selectionCount > 0 ? Math.round(selectionMs / selectionCount) : 0;

    return (
        <div className="tbp">
            <div className="tbp-header">
                <h3>Time Breakdown</h3>
            </div>

            <div className="tbp-range-row">
                <button
                    className={`tbp-range-btn${isFiltered ? ' is-active' : ''}`}
                    onClick={() => setShowCalendar((v) => !v)}
                >
                    <span className="tbp-range-icon">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                    </span>
                    <span className="tbp-range-label">{rangeLabel}</span>
                    <svg className={`tbp-range-chevron${showCalendar ? ' is-open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>
                {isFiltered && (
                    <button className="tbp-clear-btn" onClick={clearFilter} title="Clear filter">✕</button>
                )}
            </div>

            {showCalendar && (
                <div className="tbp-calendar-wrap">
                    <Calendar
                        selectRange
                        onChange={handleRangeChange}
                        value={range}
                        maxDate={new Date()}
                    />
                </div>
            )}

            {!showCalendar && (
                <>
                    {!breakdown?.loading && days.length > 0 && (
                        <div className="tbp-summary">
                            <span className="tbp-summary-total">{fmtMs(breakdown.total)}</span>
                            <span className="tbp-summary-meta">
                                across {days.length} day{days.length !== 1 ? 's' : ''}
                                {isFiltered ? ' in range' : ' total'}
                            </span>
                        </div>
                    )}

                    <div className="tbp-days-wrapper">
                        {breakdown?.loading ? (
                            <div className="tbp-state">Loading…</div>
                        ) : days.length === 0 ? (
                            <div className="tbp-state">
                                No sessions{isFiltered ? ' in this date range' : ' recorded yet'}
                            </div>
                        ) : (
                            <ul className="tbp-days">
                                {days.map((day) => (
                                    <li
                                        key={day.date}
                                        className={`tbp-day${selectedDays.has(day.date) ? ' is-selected' : ''}${expandedDays.has(day.date) ? ' is-expanded' : ''}`}
                                        onClick={() => toggleDay(day.date)}
                                    >
                                        <div className="tbp-day-main">
                                            <Checkbox
                                                checked={selectedDays.has(day.date)}
                                                onChange={() => toggleDay(day.date)}
                                            />
                                            <div className="tbp-day-info">
                                                <div className="tbp-day-head">
                                                    <div className="tbp-day-head-main">
                                                        <div className="tbp-day-label-row">
                                                            <span className="tbp-day-label">{formatDateLabel(day.date)}</span>
                                                            {day.members.length > 0 && (
                                                                <button
                                                                    type="button"
                                                                    className={`tbp-day-expand-btn${expandedDays.has(day.date) ? ' is-open' : ''}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleExpandedDay(day.date);
                                                                    }}
                                                                    aria-label={expandedDays.has(day.date) ? 'Hide member timers' : 'Show member timers'}
                                                                >
                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                        <polyline points="6 9 12 15 18 9" />
                                                                    </svg>
                                                                </button>
                                                            )}
                                                        </div>
                                                        {day.members.length > 0 && (
                                                            <span className="tbp-day-members-summary">
                                                                {day.members.length} people
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="tbp-day-head-actions">
                                                        <span className="tbp-day-total-inline">{fmtMs(day.totalDuration)}</span>
                                                    </div>
                                                </div>
                                                {expandedDays.has(day.date) && day.members.length > 0 && (
                                                    <div className="tbp-day-members">
                                                        {day.members
                                                            .slice()
                                                            .sort((a, b) => b.duration - a.duration)
                                                            .map((member) => (
                                                                <div
                                                                    key={`${day.date}-${member.userId}`}
                                                                    className="tbp-day-member"
                                                                >
                                                                    <span className="tbp-day-member-name">{member.name}</span>
                                                                    <span className="tbp-day-member-duration">
                                                                        {fmtMs(member.duration)}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {selectionCount > 0 && (
                        <div className="tbp-footer">
                            <div className="tbp-sel-stat">
                                <span className="tbp-sel-label">Sum</span>
                                <span className="tbp-sel-value">{fmtMs(selectionMs)}</span>
                            </div>
                            <div className="tbp-sel-stat">
                                <span className="tbp-sel-label">Avg / day</span>
                                <span className="tbp-sel-value">{fmtMs(selectionAvg)}</span>
                            </div>
                            <div className="tbp-sel-stat">
                                <span className="tbp-sel-label">Days</span>
                                <span className="tbp-sel-value">{selectionCount}</span>
                            </div>
                            <button
                                className="tbp-footer-clear"
                                onClick={(e) => { e.stopPropagation(); setSelectedDays(new Set()); }}
                            >✕</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default TimerBreakdownPopover;
